/**
 * URL helpers for Mitarbeiterverwaltung (overview + per-employee detail).
 */

export type EmployeeDetailTab = 'profile' | 'documents' | 'be-abzuege' | 'time-tracking';

export type EmployeeOverviewTab = 'uebersicht' | 'antraege' | 'be-abzuege' | 'lohnbuchhaltung';

const EMPLOYEE_DETAIL_TABS: EmployeeDetailTab[] = ['profile', 'documents', 'be-abzuege', 'time-tracking'];

const OVERVIEW_TABS: EmployeeOverviewTab[] = ['uebersicht', 'antraege', 'be-abzuege', 'lohnbuchhaltung'];

const instancePrefix = (): string => {
  if (typeof window === 'undefined') return '';
  const match = window.location.pathname.match(/^\/i\/[^/]+/);
  return match ? match[0] : '';
};

export function isEmployeeDetailTab(value: string | undefined): value is EmployeeDetailTab {
  return EMPLOYEE_DETAIL_TABS.includes(value as EmployeeDetailTab);
}

export function isOverviewTab(value: string | undefined): value is EmployeeOverviewTab {
  return OVERVIEW_TABS.includes(value as EmployeeOverviewTab);
}

export function employeesBasePath(instanceId?: string): string {
  const prefix = instanceId ? `/i/${encodeURIComponent(instanceId)}` : instancePrefix();
  return `${prefix}/mitarbeiter`;
}

export function employeesOverviewPath(
  instanceId?: string,
  tab: EmployeeOverviewTab = 'uebersicht',
  period?: { from?: string; to?: string },
): string {
  const base = `${employeesBasePath(instanceId)}/uebersicht/${tab}`;
  if (!period?.from && !period?.to) return base;
  const q = new URLSearchParams();
  if (period.from) q.set('from', period.from);
  if (period.to) q.set('to', period.to);
  const qs = q.toString();
  return qs ? `${base}?${qs}` : base;
}

export function parseMonthQueryParam(value: string | null | undefined): Date | null {
  const match = /^(\d{4})-(\d{2})$/.exec(String(value ?? '').trim());
  if (!match) return null;
  const year = Number.parseInt(match[1], 10);
  const month = Number.parseInt(match[2], 10);
  if (!Number.isFinite(year) || month < 1 || month > 12) return null;
  return new Date(year, month - 1, 1);
}

export function monthQueryParamFromDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export function employeeDetailPath(
  instanceId: string | undefined,
  employeeId: string,
  tab: EmployeeDetailTab = 'profile',
  query?: Record<string, string | undefined>,
): string {
  const base = `${employeesBasePath(instanceId)}/${encodeURIComponent(employeeId)}/${tab}`;
  if (!query) return base;
  const q = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value) q.set(key, value);
  }
  const qs = q.toString();
  return qs ? `${base}?${qs}` : base;
}

/**
 * Build an employee detail URL from an already-resolved instance prefix
 * (e.g. "/i/{instanceId}"). Falls back to {@link employeeDetailPath} when no
 * prefix is provided.
 */
export function employeeDetailPathFromPrefix(
  instancePrefix: string,
  employeeId: string,
  tab: EmployeeDetailTab = 'profile',
): string {
  if (!instancePrefix) return employeeDetailPath(undefined, employeeId, tab);
  return `${instancePrefix}/mitarbeiter/${encodeURIComponent(employeeId)}/${tab}`;
}

function toYmdDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** First–last day of the current calendar month. */
export function currentMonthBounds(ref = new Date()): { from: string; to: string } {
  const from = new Date(ref.getFullYear(), ref.getMonth(), 1);
  const to = new Date(ref.getFullYear(), ref.getMonth() + 1, 0);
  return { from: toYmdDate(from), to: toYmdDate(to) };
}

export function previousMonthBounds(): { from: string; to: string } {
  const now = new Date();
  const from = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const to = new Date(now.getFullYear(), now.getMonth(), 0);
  return { from: toYmdDate(from), to: toYmdDate(to) };
}

export function currentWeekBounds(ref = new Date()): { from: string; to: string } {
  const d = new Date(ref.getFullYear(), ref.getMonth(), ref.getDate());
  const day = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - day);
  const from = new Date(d);
  const to = new Date(d);
  to.setDate(to.getDate() + 6);
  const toYmd = (x: Date) => {
    const y = x.getFullYear();
    const m = String(x.getMonth() + 1).padStart(2, '0');
    const day = String(x.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };
  return { from: toYmd(from), to: toYmd(to) };
}

export function formatPeriodLabel(from: string, to: string): string {
  const fmt = (ymd: string) => {
    const [y, m, d] = ymd.slice(0, 10).split('-');
    return `${d}.${m}.${y}`;
  };
  return `${fmt(from)} – ${fmt(to)}`;
}

export function formatPeriodMonthTitle(from: string): string {
  const d = new Date(`${from.slice(0, 10)}T12:00:00`);
  if (Number.isNaN(d.getTime())) return from;
  return d.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
}

export function periodDaysProgress(from: string, to: string, ref = new Date()): { covered: number; total: number } {
  const parse = (ymd: string) => new Date(`${ymd.slice(0, 10)}T12:00:00`);
  const start = parse(from);
  const end = parse(to);
  const today = new Date(ref.getFullYear(), ref.getMonth(), ref.getDate(), 12);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return { covered: 0, total: 0 };
  const dayMs = 86400000;
  const total = Math.max(1, Math.round((end.getTime() - start.getTime()) / dayMs) + 1);
  const coveredEnd = today.getTime() < end.getTime() ? today : end;
  const covered = Math.max(0, Math.min(total, Math.round((coveredEnd.getTime() - start.getTime()) / dayMs) + 1));
  return { covered, total };
}
